import { useState, useEffect } from 'react'
import { getDataAPI, postDataAPI, putDataAPI } from '../lib/api'
import Button from '../components/ui/Button'
import Modal from '../components/ui/Modal'
import DatePicker from '../components/ui/DatePicker'

interface FinancialEntry {
  id: string
  date: string
  due_date?: string
  type: 'revenue' | 'spend'
  amount: number
  description?: string
  // Source is kept for backend compatibility, but UI prefers category
  source?: string
  vendor?: string
  category?: string
  is_paid?: boolean
  paid_date?: string
}

// Helper to format date as YYYY-MM-DD in local timezone
function formatLocalDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/* Unused date helper functions - kept for future use
function getToday() {
  return formatLocalDate(new Date())
}

function getWeekStart() {
  const today = new Date()
  const day = today.getDay()
  const diff = today.getDate() - day
  const weekStart = new Date(today)
  weekStart.setDate(diff)
  return formatLocalDate(weekStart)
}

function getMonthStart() {
  const today = new Date()
  return formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1))
}
*/

// Mock data for demonstration (~$150k month) - generates last 30 days
/* Unused mock data generator - kept for future use
const generateMockFinancials = (): FinancialEntry[] => {
  const today = new Date()
  const entries: FinancialEntry[] = []
  let mockId = 1
  
  // Generate entries for the last 30 days
  const addEntry = (
    daysAgo: number,
    type: 'revenue' | 'spend',
    amount: number,
    description: string,
    source: string,
    vendor?: string,
    category?: string,
    dueDateOffsetDays?: number,
    isPaid?: boolean
  ) => {
    const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysAgo)
    const dueDate =
      type === 'spend' && typeof dueDateOffsetDays === 'number'
        ? formatLocalDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + dueDateOffsetDays))
        : undefined
    entries.push({
      id: `mock-${mockId++}`,
      date: formatLocalDate(date),
      due_date: dueDate,
      type,
      amount,
      description,
      source,
      vendor,
      category,
      is_paid: !!isPaid,
      paid_date: isPaid ? formatLocalDate(date) : undefined,
    })
  }
  
  // Today (0 days ago)
  addEntry(0, 'revenue', 15000.00, 'Performance guarantee - Terminal 5 NYC', 'shows', 'Terminal 5', 'shows')
  addEntry(0, 'revenue', 4850.00, 'Merch sales - Terminal 5', 'commerce', 'Shopify / POS', 'merch')
  addEntry(0, 'spend', 750.00, 'Sound engineer day rate', 'shows', 'Freelance Engineer', 'production', 7, false)
  addEntry(0, 'spend', 425.00, 'Backline rental', 'shows', 'Backline Co.', 'production', 14, false)
  
  // Yesterday (1 day ago)
  addEntry(1, 'revenue', 8750.00, 'Streaming royalties - monthly', 'music', 'DSP Aggregator', 'royalties')
  addEntry(1, 'revenue', 2400.00, 'YouTube AdSense', 'music', 'YouTube', 'royalties')
  addEntry(1, 'spend', 350.00, 'Studio session - mixing', 'music', 'Studio A', 'production', 10)
  
  // 2 days ago
  addEntry(2, 'revenue', 12500.00, 'Performance guarantee - Brooklyn Steel', 'shows', 'Brooklyn Steel', 'shows')
  addEntry(2, 'revenue', 3650.00, 'Merch sales - Brooklyn Steel', 'commerce', 'Shopify / POS', 'merch')
  addEntry(2, 'spend', 1200.00, 'Production costs', 'shows', 'Production Vendor', 'production', 14)
  
  // 3 days ago
  addEntry(3, 'revenue', 18000.00, 'Festival appearance - Governors Ball', 'shows', 'Governors Ball', 'shows')
  addEntry(3, 'revenue', 6200.00, 'Merch sales - festival booth', 'commerce', 'Festival POS', 'merch')
  addEntry(3, 'spend', 2500.00, 'Travel & lodging - NYC', 'shows', 'Marriott', 'lodging', 3)
  
  // 4 days ago
  addEntry(4, 'revenue', 3500.00, 'Sync license - Netflix series', 'music', 'Music Supervisor', 'royalties')
  addEntry(4, 'spend', 800.00, 'PR retainer', 'manual', 'PR Agency', 'marketing', 30, true)
  
  // 5 days ago
  addEntry(5, 'revenue', 9500.00, 'Performance guarantee - 9:30 Club DC', 'shows', '9:30 Club', 'shows')
  addEntry(5, 'revenue', 2850.00, 'Merch sales - DC', 'commerce', 'Shopify / POS', 'merch')
  addEntry(5, 'spend', 650.00, 'Hotel - DC', 'shows', 'Hilton', 'lodging', 7)
  
  // 6 days ago
  addEntry(6, 'revenue', 1250.00, 'Beat placements', 'music', 'BeatStars', 'royalties')
  addEntry(6, 'spend', 500.00, 'Social media ads', 'manual', 'Meta Ads', 'marketing', 5)
  
  // 7 days ago
  addEntry(7, 'revenue', 11000.00, 'Performance guarantee - The Fillmore Philly', 'shows', 'The Fillmore', 'shows')
  addEntry(7, 'revenue', 3100.00, 'Merch sales - Philly', 'commerce', 'Shopify / POS', 'merch')
  addEntry(7, 'spend', 1800.00, 'Tour bus - weekly', 'shows', 'Bus Co.', 'travel', 14)
  addEntry(7, 'spend', 450.00, 'Per diems - crew', 'shows', 'Crew', 'payroll', 0, true)
  
  // 9 days ago
  addEntry(9, 'revenue', 2200.00, 'Vinyl sales - Bandcamp', 'commerce', 'Bandcamp', 'merch')
  addEntry(9, 'spend', 275.00, 'Shipping supplies', 'commerce', 'Uline', 'fees', 14)
  
  // 11 days ago
  addEntry(11, 'revenue', 8500.00, 'Performance guarantee - Paradise Rock Club', 'shows', 'Paradise Rock Club', 'shows')
  addEntry(11, 'revenue', 2400.00, 'Merch sales - Boston', 'commerce', 'Shopify / POS', 'merch')
  addEntry(11, 'spend', 550.00, 'Hotel - Boston', 'shows', 'Hyatt', 'lodging', 7)
  
  // 13 days ago
  addEntry(13, 'revenue', 5500.00, 'Brand partnership - Instagram', 'manual', 'Brand Partner', 'other')
  addEntry(13, 'spend', 1500.00, 'Music video production', 'music', 'Video House', 'production', 30)
  
  // 15 days ago
  addEntry(15, 'revenue', 10500.00, 'Performance guarantee - House of Blues Chicago', 'shows', 'House of Blues', 'shows')
  addEntry(15, 'revenue', 4200.00, 'Merch sales - Chicago', 'commerce', 'Shopify / POS', 'merch')
  addEntry(15, 'spend', 2200.00, 'Flights - band + crew', 'shows', 'United', 'travel', 7)
  addEntry(15, 'spend', 850.00, 'Hotel - Chicago', 'shows', 'Marriott', 'lodging', 10)
  
  // 17 days ago
  addEntry(17, 'revenue', 1800.00, 'Sample clearance payment received', 'music', 'Publisher', 'fees')
  addEntry(17, 'spend', 3500.00, 'Sample clearance fee paid', 'music', 'Publisher', 'fees', 0)
  
  // 19 days ago
  addEntry(19, 'revenue', 7500.00, 'Performance guarantee - First Avenue Minneapolis', 'shows', 'First Avenue', 'shows')
  addEntry(19, 'revenue', 2100.00, 'Merch sales - Minneapolis', 'commerce', 'Shopify / POS', 'merch')
  addEntry(19, 'spend', 600.00, 'Equipment maintenance', 'shows', 'Repair Shop', 'production', 14)
  
  // 21 days ago
  addEntry(21, 'revenue', 12000.00, 'Advance - upcoming album', 'music', 'Label', 'other')
  addEntry(21, 'spend', 4000.00, 'Studio block booking', 'music', 'Studio A', 'production', 14)
  
  // 23 days ago
  addEntry(23, 'revenue', 9000.00, 'Performance guarantee - The Depot SLC', 'shows', 'The Depot', 'shows')
  addEntry(23, 'revenue', 2650.00, 'Merch sales - Salt Lake', 'commerce', 'Shopify / POS', 'merch')
  addEntry(23, 'spend', 1100.00, 'Tour van rental', 'shows', 'Van Co.', 'travel', 14)
  
  // 25 days ago
  addEntry(25, 'revenue', 3200.00, 'Patreon monthly', 'manual', 'Patreon', 'other')
  addEntry(25, 'revenue', 850.00, 'Cameo earnings', 'manual', 'Cameo', 'other')
  addEntry(25, 'spend', 200.00, 'Patreon rewards fulfillment', 'manual', 'USPS', 'fees', 7)
  
  // 27 days ago
  addEntry(27, 'revenue', 14000.00, 'Performance guarantee - The Ogden Denver', 'shows', 'The Ogden', 'shows')
  addEntry(27, 'revenue', 4500.00, 'Merch sales - Denver', 'commerce', 'Shopify / POS', 'merch')
  addEntry(27, 'spend', 750.00, 'Hotel - Denver', 'shows', 'Hyatt', 'lodging', 7)
  addEntry(27, 'spend', 350.00, 'Uber/transport', 'shows', 'Uber', 'travel', 0)
  
  // 29 days ago
  addEntry(29, 'revenue', 6500.00, 'Publishing royalties - quarterly', 'music', 'PRO', 'royalties')
  addEntry(29, 'revenue', 1500.00, 'TikTok creator fund', 'music', 'TikTok', 'royalties')
  addEntry(29, 'spend', 1200.00, 'Manager commission', 'manual', 'Management', 'fees', 0)
  addEntry(29, 'spend', 800.00, 'Accountant fees', 'manual', 'Accountant', 'fees', 30)
  
  return entries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}
*/

export default function FinancePage() {
  const [financialEntries, setFinancialEntries] = useState<FinancialEntry[]>([])
  const [_isLoading, setIsLoading] = useState(true)
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [paidSaving, setPaidSaving] = useState<Record<string, boolean>>({})
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editEntryId, setEditEntryId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set())
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [formData, setFormData] = useState({
    date: formatLocalDate(new Date()),
    due_date: '',
    type: 'revenue' as 'revenue' | 'spend',
    amount: 0,
    description: '',
    vendor: '',
    category: 'other',
  })

  useEffect(() => {
    const fetchFinancials = async () => {
      try {
        const response = await getDataAPI('financials')
        const data = response.data || response
        setFinancialEntries(Array.isArray(data) ? data : [])
      } catch (error) {
        console.error('Error fetching financial entries:', error)
        setFinancialEntries([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchFinancials()
  }, [])

  const refresh = async () => {
    try {
      const response = await getDataAPI('financials')
      const data = response.data || response
      setFinancialEntries(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error refreshing financial entries:', error)
    }
  }

  const togglePaid = async (entry: FinancialEntry) => {
    const id = entry.id
    if (!id) return
    const nextPaid = !entry.is_paid
    const nextPaidDate = nextPaid ? formatLocalDate(new Date()) : undefined

    // Optimistic UI update
    setFinancialEntries((prev) =>
      prev.map((e) =>
        e.id === id ? { ...e, is_paid: nextPaid, paid_date: nextPaidDate } : e
      )
    )

    setPaidSaving((prev) => ({ ...prev, [id]: true }))
    try {
      await putDataAPI('financials', {
        entryId: id,
        data: { is_paid: nextPaid, paid_date: nextPaidDate },
      })
    } catch (error) {
      console.error('Failed to persist paid toggle:', error)
      // Revert optimistic update
      setFinancialEntries((prev) =>
        prev.map((e) =>
          e.id === id ? { ...e, is_paid: entry.is_paid, paid_date: entry.paid_date } : e
        )
      )
    } finally {
      setPaidSaving((prev) => ({ ...prev, [id]: false }))
    }
  }

  const resetForm = () => {
    setFormData({
      date: formatLocalDate(new Date()),
      due_date: '',
      type: 'revenue' as 'revenue' | 'spend',
      amount: 0,
      description: '',
      vendor: '',
      category: 'other',
    })
    setIsEditing(false)
    setEditEntryId(null)
  }

  const startEditEntry = (entry: FinancialEntry) => {
    setFormData({
      date: entry.date,
      due_date: entry.due_date || '',
      type: entry.type,
      amount: entry.amount,
      description: entry.description || '',
      vendor: entry.vendor || '',
      category: entry.category || 'other',
    })
    setIsEditing(true)
    setEditEntryId(entry.id)
    setIsAddOpen(true)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const payload = {
        date: formData.date,
        due_date: formData.due_date || undefined,
        type: formData.type,
        amount: formData.amount,
        description: formData.description?.trim() || undefined,
        vendor: formData.vendor?.trim() || undefined,
        category: formData.category || undefined,
      }

      if (isEditing && editEntryId) {
        // Update existing entry
        await putDataAPI('financials', {
          entryId: editEntryId,
          data: payload,
        })
      } else {
        // Create new entry
        await postDataAPI('financials', payload)
      }

      setIsAddOpen(false)
      resetForm()
      await refresh()
    } catch (error) {
      console.error('Failed to save financial entry:', error)
      alert('Failed to save entry. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteEntry = async (entryId: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return
    try {
      await putDataAPI('financials/delete', { entryId })
      await refresh()
      setOpenDropdown(null)
    } catch (error) {
      console.error('Failed to delete entry:', error)
      alert('Failed to delete entry. Please try again.')
    }
  }

  const toggleEntrySelection = (entryId: string) => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev)
      if (next.has(entryId)) next.delete(entryId)
      else next.add(entryId)
      return next
    })
  }

  const selectAllFiltered = () => {
    const ids = filteredEntries.map((e) => e.id)
    if (ids.length === 0) return
    setSelectedEntryIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id))
      if (allSelected) {
        const next = new Set(prev)
        ids.forEach((id) => next.delete(id))
        return next
      }
      const next = new Set(prev)
      ids.forEach((id) => next.add(id))
      return next
    })
  }

  const handleBulkDelete = async () => {
    if (selectedEntryIds.size === 0) return
    if (!window.confirm(`Delete ${selectedEntryIds.size} selected ${selectedEntryIds.size === 1 ? 'entry' : 'entries'}? This cannot be undone.`)) return
    setIsBulkDeleting(true)
    
    const idsToDelete = Array.from(selectedEntryIds)
    let successCount = 0
    let errorCount = 0
    
    try {
      for (const entryId of idsToDelete) {
        try {
          await putDataAPI('financials/delete', { entryId })
          successCount++
        } catch (error) {
          console.error(`Failed to delete entry ${entryId}:`, error)
          errorCount++
        }
      }
      
      setFinancialEntries((prev) => prev.filter((e) => !selectedEntryIds.has(e.id)))
      setSelectedEntryIds(new Set())
      setOpenDropdown(null)
      
      if (errorCount > 0) {
        alert(`Deleted ${successCount} ${successCount === 1 ? 'entry' : 'entries'}. ${errorCount} failed.`)
      }
      
      await refresh()
    } catch (error) {
      console.error('Bulk delete failed:', error)
      alert('Failed to delete entries. Please try again.')
    } finally {
      setIsBulkDeleting(false)
    }
  }

  // Rolling periods so totals match the visible “recent entries”
  // - This Week: last 7 days (including today)
  // - This Month: last 30 days (including today)
  // - YTD: Jan 1st -> today
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const todayEnd = todayStart + 86400000 - 1 // End of today

  const weekStartTime = todayStart - 6 * 86400000
  const monthStartTime = todayStart - 29 * 86400000
  const yearStartTime = new Date(now.getFullYear(), 0, 1).getTime()

  const parseEntryDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day).getTime()
  }

  const financialWeek = financialEntries.filter((f) => {
    const t = parseEntryDate(f.date)
    return t >= weekStartTime && t <= todayEnd
  })
  
  const financialMonth = financialEntries.filter((f) => {
    const t = parseEntryDate(f.date)
    return t >= monthStartTime && t <= todayEnd
  })
  
  const financialYear = financialEntries.filter((f) => {
    const t = parseEntryDate(f.date)
    return t >= yearStartTime && t <= todayEnd
  })

  const revenueWeek = financialWeek.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  const spendWeek = financialWeek.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)
  const revenueMonth = financialMonth.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  const spendMonth = financialMonth.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)
  const revenueYear = financialYear.filter(f => f.type === 'revenue').reduce((sum, f) => sum + f.amount, 0)
  const spendYear = financialYear.filter(f => f.type === 'spend').reduce((sum, f) => sum + f.amount, 0)

  // Apply search and filters
  const searchLower = searchQuery.trim().toLowerCase()
  const filteredEntries = financialEntries.filter((entry) => {
    if (typeFilter && entry.type !== typeFilter) return false
    if (categoryFilter && entry.category !== categoryFilter) return false
    if (!searchLower) return true
    const description = (entry.description || '').toLowerCase()
    const vendor = (entry.vendor || '').toLowerCase()
    const category = (entry.category || '').toLowerCase()
    const amount = entry.amount.toString()
    return description.includes(searchLower) || vendor.includes(searchLower) || category.includes(searchLower) || amount.includes(searchLower)
  })

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-10 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Finance</h1>
          <p className="text-sm text-gray-500">High-level financial awareness</p>
        </div>
        <Button onClick={() => setIsAddOpen(true)}>Add Entry</Button>
      </div>

      <Modal
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false)
          resetForm()
        }}
        title={isEditing ? 'Edit Financial Entry' : 'Add Financial Entry'}
        description={isEditing ? 'Update entry details.' : undefined}
        maxWidthClassName="max-w-4xl"
      >
        <form onSubmit={handleCreate} className="space-y-5">
          <div>
            <DatePicker
              label="Date"
              value={formData.date}
              onChange={(value) => setFormData({ ...formData, date: value })}
              required
            />
          </div>
          <div>
            <DatePicker
              label="Due Date"
              value={formData.due_date}
              onChange={(value) => setFormData({ ...formData, due_date: value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
              className="ds-input"
            >
              <option value="revenue">Revenue</option>
              <option value="spend">Spend</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Amount</label>
            <input
              type="number"
              step="0.01"
              required
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="ds-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Vendor</label>
            <input
              type="text"
              value={formData.vendor}
              onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
              className="ds-input"
              placeholder="Who you paid / who paid you"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="ds-input"
            >
              <option value="shows">Shows</option>
              <option value="merch">Merch</option>
              <option value="production">Production</option>
              <option value="marketing">Marketing</option>
              <option value="travel">Travel</option>
              <option value="lodging">Lodging</option>
              <option value="payroll">Payroll</option>
              <option value="fees">Fees</option>
              <option value="royalties">Royalties</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? 'Saving…' : (isEditing ? 'Save Changes' : 'Save')}
            </Button>
            <Button type="button" variant="secondary" className="flex-1" onClick={() => {
              setIsAddOpen(false)
              resetForm()
            }}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Summary Stats */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs font-medium text-gray-500 mb-4 uppercase tracking-wider">This Week</div>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Revenue</div>
              <div className="text-xl font-semibold text-gray-900">${revenueWeek.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Spend</div>
              <div className="text-xl font-semibold text-gray-900">${spendWeek.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-1">Net</div>
              <div className={`text-xl font-semibold ${(revenueWeek - spendWeek) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${(revenueWeek - spendWeek).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs font-medium text-gray-500 mb-4 uppercase tracking-wider">This Month</div>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Revenue</div>
              <div className="text-xl font-semibold text-gray-900">${revenueMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Spend</div>
              <div className="text-xl font-semibold text-gray-900">${spendMonth.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-1">Net</div>
              <div className={`text-xl font-semibold ${(revenueMonth - spendMonth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${(revenueMonth - spendMonth).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="text-xs font-medium text-gray-500 mb-4 uppercase tracking-wider">YTD (2026)</div>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Revenue</div>
              <div className="text-xl font-semibold text-gray-900">${revenueYear.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Spend</div>
              <div className="text-xl font-semibold text-gray-900">${spendYear.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="text-xs text-gray-500 mb-1">Net</div>
              <div className={`text-xl font-semibold ${(revenueYear - spendYear) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${(revenueYear - spendYear).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      {financialEntries.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by description, vendor, category, or amount..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="revenue">Revenue</option>
            <option value="spend">Spend</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 focus:border-transparent"
          >
            <option value="">All Categories</option>
            <option value="shows">Shows</option>
            <option value="merch">Merch</option>
            <option value="production">Production</option>
            <option value="marketing">Marketing</option>
            <option value="travel">Travel</option>
            <option value="lodging">Lodging</option>
            <option value="payroll">Payroll</option>
            <option value="fees">Fees</option>
            <option value="royalties">Royalties</option>
            <option value="other">Other</option>
          </select>
          {(searchQuery.trim() || typeFilter || categoryFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                setTypeFilter('')
                setCategoryFilter('')
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear filters
            </button>
          )}
          <span className="text-sm text-gray-500">
            {filteredEntries.length} of {financialEntries.length} {financialEntries.length === 1 ? 'entry' : 'entries'}
          </span>
          {filteredEntries.length > 0 ? (
            <div className="flex items-center gap-3 ml-auto">
              {selectedEntryIds.size === 0 ? (
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                >
                  Select all
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedEntryIds(new Set())}
                    className="text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                  >
                    Clear
                  </button>
                  <Button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 px-3"
                  >
                    {isBulkDeleting ? 'Deleting…' : `Delete ${selectedEntryIds.size} selected`}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Recent Entries */}
      <div>
        <h2 className="text-sm font-medium mb-4 text-gray-900">Recent Entries</h2>
        {financialEntries.length > 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Due</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Paid</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Vendor</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider">Description</th>
                  <th className="py-3 px-4 text-xs font-medium text-gray-700 uppercase tracking-wider"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEntries.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-sm text-gray-500">
                      No entries match your search or filters. Try adjusting or{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery('')
                          setTypeFilter('')
                          setCategoryFilter('')
                        }}
                        className="text-gray-900 font-medium hover:underline"
                      >
                        clear filters
                      </button>
                      .
                    </td>
                  </tr>
                ) : (
                  filteredEntries.slice(0, 50).map((entry) => {
                    const isSelected = selectedEntryIds.has(entry.id)
                    return (
                  <tr 
                    key={entry.id} 
                    className={`transition-colors hover:bg-gray-50/50 dark:hover:bg-white/[0.04] cursor-pointer ${isSelected ? 'bg-gray-100 dark:bg-white/[0.08]' : ''}`}
                    onClick={() => toggleEntrySelection(entry.id)}
                  >
                    <td className="py-3 px-4 text-sm text-gray-900">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {entry.due_date ? new Date(entry.due_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {entry.due_date ? (
                        <button
                          type="button"
                          onClick={() => void togglePaid(entry)}
                          disabled={!!paidSaving[entry.id]}
                          className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
                            entry.is_paid
                              ? 'bg-green-50 text-green-700 border-green-100 hover:bg-green-100 dark:bg-green-500/15 dark:text-green-200 dark:border-green-500/25 dark:hover:bg-green-500/20'
                              : 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200 dark:bg-white/10 dark:text-white/85 dark:border-white/15 dark:hover:bg-white/15'
                          } ${paidSaving[entry.id] ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title={entry.is_paid ? `Paid${entry.paid_date ? ` (${new Date(entry.paid_date).toLocaleDateString()})` : ''}` : 'Mark as paid'}
                        >
                          <span className={`h-2.5 w-2.5 rounded-full ${entry.is_paid ? 'bg-green-600 dark:bg-green-300' : 'bg-gray-500 dark:bg-white/60'}`} />
                          {entry.is_paid ? 'Paid' : 'Unpaid'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{entry.vendor || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{entry.category || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        entry.type === 'revenue' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {entry.type}
                      </span>
                    </td>
                    <td className={`py-3 px-4 text-sm text-right font-medium ${
                      entry.type === 'revenue' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ${entry.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{entry.description || '-'}</td>
                    <td className="py-3 px-4 text-center relative" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setOpenDropdown(openDropdown === entry.id ? null : entry.id)
                          }}
                          className="text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 rounded p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        {openDropdown === entry.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20 py-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  startEditEntry(entry)
                                  setOpenDropdown(null)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteEntry(entry.id)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-sm text-gray-500">No financial entries found</p>
          </div>
        )}
      </div>
    </div>
  )
}
